<script setup>
// Desfechos do Playbook (Fase 3, tela 02, spec §4.3): CRUD simples por assistente (REST captain outcomes).
// Cada desfecho: título, condição em linguagem natural e os dados exigidos (das definições com polaris_collect).
// A escrita é admin-only (policy do backend); para agente a seção é só leitura.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useMapGetter } from 'dashboard/composables/store';
import PolarisOutcomesAPI from 'dashboard/api/polaris/outcomes';

import { Button } from 'dashboard/components-next/polaris/ui/button';
import { Input } from 'dashboard/components-next/polaris/ui/input';
import { Textarea } from 'dashboard/components-next/polaris/ui/textarea';
import { Label } from 'dashboard/components-next/polaris/ui/label';
import { Checkbox } from 'dashboard/components-next/polaris/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'dashboard/components-next/polaris/ui/dialog';
import DeleteDialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  assistantId: { type: Number, required: true },
});

const { t } = useI18n();
const { isAdmin } = useAdmin();

const outcomes = ref([]);
const isFetching = ref(true);
const isSaving = ref(false);
const dialogOpen = ref(false);
const editing = ref(null);
const deleteDialogRef = ref(null);
const toDelete = ref(null);

function emptyForm() {
  return {
    key: '',
    title: '',
    condition_text: '',
    required_attribute_keys: [],
    // Fase 7 (funil de Desempenho): o desfecho conta como "lead qualificado".
    qualifies: false,
  };
}

const form = ref(emptyForm());

const attributes = useMapGetter('attributes/getContactAttributes');
const collectable = computed(() =>
  (attributes.value || []).filter(item => item.polarisCollect)
);

const fetchOutcomes = async () => {
  isFetching.value = true;
  try {
    const { data } = await PolarisOutcomesAPI.get({
      assistantId: props.assistantId,
    });
    outcomes.value = data.payload;
  } catch {
    useAlert(t('POLARIS.PLAYBOOK.OUTCOMES.API.ERROR'));
  } finally {
    isFetching.value = false;
  }
};

// A página do Roteiro (pai) busca os atributos uma vez para as duas seções.
onMounted(fetchOutcomes);

const openEditor = outcome => {
  editing.value = outcome;
  form.value = outcome
    ? {
        key: outcome.key,
        title: outcome.title,
        condition_text: outcome.condition_text || '',
        required_attribute_keys: [...outcome.required_attribute_keys],
        qualifies: outcome.qualifies === true,
      }
    : emptyForm();
  dialogOpen.value = true;
};

const toggleRequired = (key, checked) => {
  const current = new Set(form.value.required_attribute_keys);
  if (checked) current.add(key);
  else current.delete(key);
  form.value.required_attribute_keys = [...current];
};

const save = async () => {
  isSaving.value = true;
  try {
    const payload = { assistantId: props.assistantId, ...form.value };
    if (editing.value) {
      await PolarisOutcomesAPI.update({ ...payload, id: editing.value.id });
    } else {
      await PolarisOutcomesAPI.create(payload);
    }
    dialogOpen.value = false;
    await fetchOutcomes();
    useAlert(t('POLARIS.PLAYBOOK.OUTCOMES.API.SUCCESS'));
  } catch {
    useAlert(t('POLARIS.PLAYBOOK.OUTCOMES.API.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const confirmDelete = outcome => {
  toDelete.value = outcome;
  deleteDialogRef.value.dialogRef.open();
};

const remove = async () => {
  try {
    await PolarisOutcomesAPI.delete({
      assistantId: props.assistantId,
      id: toDelete.value.id,
    });
    await fetchOutcomes();
    useAlert(t('POLARIS.PLAYBOOK.OUTCOMES.API.DELETE_SUCCESS'));
  } catch {
    useAlert(t('POLARIS.PLAYBOOK.OUTCOMES.API.ERROR'));
  }
};

const keyFromTitle = () => {
  if (editing.value || form.value.key) return;
  form.value.key = form.value.title
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};
</script>

<template>
  <section class="flex flex-col gap-3">
    <div class="flex items-center justify-between gap-2">
      <h2 class="m-0 text-base font-semibold text-polaris-foreground">
        {{ t('POLARIS.PLAYBOOK.OUTCOMES.TITLE') }}
      </h2>
      <Button
        v-if="isAdmin"
        variant="outline"
        size="sm"
        @click="openEditor(null)"
      >
        {{ t('POLARIS.PLAYBOOK.OUTCOMES.ADD') }}
      </Button>
    </div>
    <p class="m-0 text-sm text-polaris-muted-foreground">
      {{ t('POLARIS.PLAYBOOK.OUTCOMES.DESCRIPTION') }}
    </p>
    <div
      v-if="outcomes.length"
      class="rounded-polaris-lg border border-solid border-polaris-border bg-polaris-card"
    >
      <div
        v-for="outcome in outcomes"
        :key="outcome.id"
        class="flex items-start justify-between gap-3 border-0 border-b border-solid border-polaris-border px-4 py-3 last:border-b-0"
      >
        <div class="flex min-w-0 flex-col gap-1">
          <div class="flex items-center gap-2">
            <span class="text-sm font-medium text-polaris-foreground">
              {{ outcome.title }}
            </span>
            <span
              class="rounded-polaris-md bg-polaris-muted px-1.5 py-0.5 text-xs text-polaris-muted-foreground"
            >
              {{ outcome.key }}
            </span>
            <span
              v-if="outcome.qualifies"
              class="rounded-polaris-md bg-polaris-success px-1.5 py-0.5 text-xs text-polaris-success-foreground"
            >
              {{ t('POLARIS.PLAYBOOK.OUTCOMES.QUALIFIES_BADGE') }}
            </span>
          </div>
          <p
            v-if="outcome.condition_text"
            class="m-0 text-sm text-polaris-muted-foreground"
          >
            {{ outcome.condition_text }}
          </p>
          <div
            v-if="outcome.required_attribute_keys.length"
            class="flex flex-wrap gap-1"
          >
            <span
              v-for="key in outcome.required_attribute_keys"
              :key="key"
              class="rounded-polaris-md bg-polaris-accent px-1.5 py-0.5 text-xs text-polaris-accent-foreground"
            >
              {{ key }}
            </span>
          </div>
        </div>
        <div v-if="isAdmin" class="flex shrink-0 gap-1">
          <Button variant="ghost" size="sm" @click="openEditor(outcome)">
            {{ t('POLARIS.PLAYBOOK.OUTCOMES.EDIT') }}
          </Button>
          <Button variant="ghost" size="sm" @click="confirmDelete(outcome)">
            {{ t('POLARIS.PLAYBOOK.OUTCOMES.DELETE') }}
          </Button>
        </div>
      </div>
    </div>
    <p
      v-else-if="!isFetching"
      class="m-0 text-sm text-polaris-muted-foreground"
    >
      {{ t('POLARIS.PLAYBOOK.OUTCOMES.EMPTY') }}
    </p>

    <Dialog v-model:open="dialogOpen">
      <DialogContent class="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {{
              editing
                ? t('POLARIS.PLAYBOOK.OUTCOMES.FORM.EDIT_TITLE')
                : t('POLARIS.PLAYBOOK.OUTCOMES.FORM.NEW_TITLE')
            }}
          </DialogTitle>
          <DialogDescription>
            {{ t('POLARIS.PLAYBOOK.OUTCOMES.FORM.SUBTITLE') }}
          </DialogDescription>
        </DialogHeader>
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-1.5">
            <Label for="outcome-title">
              {{ t('POLARIS.PLAYBOOK.OUTCOMES.FORM.TITLE') }}
            </Label>
            <Input
              id="outcome-title"
              v-model="form.title"
              @blur="keyFromTitle"
            />
          </div>
          <div class="flex flex-col gap-1.5">
            <Label for="outcome-key">
              {{ t('POLARIS.PLAYBOOK.OUTCOMES.FORM.KEY') }}
            </Label>
            <Input
              id="outcome-key"
              v-model="form.key"
              :disabled="!!editing"
              :placeholder="t('POLARIS.PLAYBOOK.OUTCOMES.FORM.KEY_HINT')"
            />
          </div>
          <div class="flex flex-col gap-1.5">
            <Label for="outcome-condition">
              {{ t('POLARIS.PLAYBOOK.OUTCOMES.FORM.CONDITION') }}
            </Label>
            <Textarea
              id="outcome-condition"
              v-model="form.condition_text"
              rows="3"
            />
          </div>
          <div v-if="collectable.length" class="flex flex-col gap-1.5">
            <Label>{{ t('POLARIS.PLAYBOOK.OUTCOMES.FORM.REQUIRES') }}</Label>
            <label
              v-for="attribute in collectable"
              :key="attribute.id"
              class="flex items-center gap-2 text-sm text-polaris-foreground"
            >
              <Checkbox
                :model-value="
                  form.required_attribute_keys.includes(attribute.attributeKey)
                "
                @update:model-value="
                  checked => toggleRequired(attribute.attributeKey, checked)
                "
              />
              {{ attribute.attributeDisplayName }}
            </label>
          </div>
          <label
            class="flex items-center gap-2 text-sm text-polaris-foreground"
          >
            <Checkbox v-model="form.qualifies" />
            {{ t('POLARIS.PLAYBOOK.OUTCOMES.FORM.QUALIFIES') }}
          </label>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            :disabled="isSaving"
            @click="dialogOpen = false"
          >
            {{ t('POLARIS.PLAYBOOK.OUTCOMES.FORM.CANCEL') }}
          </Button>
          <Button
            :disabled="!form.title.trim() || !form.key.trim() || isSaving"
            @click="save"
          >
            {{ t('POLARIS.PLAYBOOK.OUTCOMES.FORM.SAVE') }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <DeleteDialog
      v-if="toDelete"
      ref="deleteDialogRef"
      type="alert"
      width="md"
      :title="t('POLARIS.PLAYBOOK.OUTCOMES.DELETE_TITLE')"
      :description="
        t('POLARIS.PLAYBOOK.OUTCOMES.DELETE_TEXT', { title: toDelete.title })
      "
      :cancel-button-label="t('POLARIS.PLAYBOOK.OUTCOMES.FORM.CANCEL')"
      :confirm-button-label="t('POLARIS.PLAYBOOK.OUTCOMES.DELETE')"
      @confirm="remove"
      @close="toDelete = null"
    />
  </section>
</template>
